import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import withRoleAccess from '../../hoc/withRoleAccess';
import { jwtDecode } from "jwt-decode";
import { useTranslation } from 'react-i18next';
import {
  Card,
  Table,
  Typography,
  Space,
  Tag,
  Button,
  message,
  Layout,
  Modal,
  Form,
  Select,
  Input,
  Tabs,
  Badge,
  Tooltip,
  Col,
  Row,
  Divider,
  DatePicker
} from 'antd';
import {
  CalendarOutlined,
  PauseOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  HourglassOutlined,
  PauseCircleOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';

const { Text, Title } = Typography;
const { Content } = Layout;
const { Option } = Select;
const { TabPane } = Tabs;

function AdminLeaveManagement() {
  const { t } = useTranslation();
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [pauseRequests, setPauseRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionModalVisible, setActionModalVisible] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [requestType, setRequestType] = useState('');
  const [actionForm] = Form.useForm();
  const [leaveSearchText, setLeaveSearchText] = useState('');
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [branches, setBranches] = useState([]);
  const [leaveStatusFilter, setLeaveStatusFilter] = useState('all');
  const [leaveDateRange, setLeaveDateRange] = useState([]);
  const [pauseDateRange, setPauseDateRange] = useState([]);
  const [pauseSearchText, setPauseSearchText] = useState('');
  const [pauseStatusFilter, setPauseStatusFilter] = useState('all');  
     const token = localStorage.getItem("token");
        let userRole = null;
        let userBranchId = null;
    
        if (token) {
        try {
            const decoded = jwtDecode(token);
            userRole = decoded.role;
            // Support both formats just in case
            userBranchId = decoded.branchId || decoded.branch_id || null;
        } catch (err) {
            console.error("Invalid token", err);
        }
        }
  
  const API_URL = process.env.REACT_APP_API_URL;

useEffect(() => {
  const fetchBranches = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_URL}/branch`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBranches(res.data);
    } catch (err) {
      console.error("❌ Failed to fetch branches", err);
    }
  };

  if (userRole === "superadmin") {
    fetchBranches();
  }
}, [API_URL, userRole]);


 // Fetch leave requests
const fetchLeaveRequests = useCallback(async () => {
  setLoading(true);
  try {
    const token = localStorage.getItem('token');

    const params = {};
    if (userRole === 'superadmin' && selectedBranch) {
      params.branch_id = selectedBranch; // ✅ only send if superadmin selected
    }

    const response = await axios.get(
      `${API_URL}/leave-requests/admin/leave-requests`,
      {
        headers: { Authorization: `Bearer ${token}` },
        params, // send query param only for superadmin
      }
    );

    setLeaveRequests(response.data || []);
  } catch (error) {
    console.error('Error fetching leave requests:', error);
    message.error(t('Error fetching leave requests'));
  } finally {
    setLoading(false);
  }
}, [API_URL, t, selectedBranch, userRole]);

// Fetch pause requests
const fetchPauseRequests = useCallback(async () => {
  setLoading(true);
  try {
    const token = localStorage.getItem('token');

    const params = {};
    if (userRole === 'superadmin' && selectedBranch) {
      params.branch_id = selectedBranch; // ✅ superadmin only
    }

    const response = await axios.get(
      `${API_URL}/leave-requests/admin/pause-requests`,
      {
        headers: { Authorization: `Bearer ${token}` },
        params,
      }
    );

    setPauseRequests(response.data || []);
  } catch (error) {
    console.error('Error fetching pause requests:', error);
    message.error(t('Error fetching pause requests'));
  } finally {
    setLoading(false);
  }
}, [API_URL, t, selectedBranch, userRole]);

useEffect(() => {
  fetchLeaveRequests();
  fetchPauseRequests();
}, [fetchLeaveRequests, fetchPauseRequests]);


// Handle status update
const handleStatusUpdate = async (values) => {
  try {
    const token = localStorage.getItem('token');
    const endpoint = requestType === 'leave' 
      ? `${API_URL}/leave-requests/admin/leave-requests/${selectedRequest.id}`
      : `${API_URL}/leave-requests/admin/pause-requests/${selectedRequest.id}`;

    await axios.put(endpoint, values, {
      headers: { Authorization: `Bearer ${token}` }
    });

    message.success(t('Request updated successfully'));
    setActionModalVisible(false);
    actionForm.resetFields();
    
    if (requestType === 'leave') {
      fetchLeaveRequests();
    } else {
      fetchPauseRequests();
    }
  } catch (error) {
    console.error('❌ Error updating request:', error.response?.data || error.message);
    message.error(t('Error updating request'));
  }
};


const filteredLeaveRequests = useMemo(() => {
  return leaveRequests.filter(request => {
    const matchesSearch =
      leaveSearchText === '' ||
      request.student_name?.toLowerCase().includes(leaveSearchText.toLowerCase()) ||
      request.student_nickname?.toLowerCase().includes(leaveSearchText.toLowerCase());

    const matchesStatus =
      leaveStatusFilter === 'all' || request.status === leaveStatusFilter;

    const matchesBranch =
      !selectedBranch || request.branch_id === selectedBranch;

    const matchesDate =
      !Array.isArray(leaveDateRange) || leaveDateRange.length === 0 ||
      (dayjs(request.original_date).isSameOrAfter(leaveDateRange[0], 'day') &&
      dayjs(request.original_date).isSameOrBefore(leaveDateRange[1], 'day'));


    return matchesSearch && matchesStatus && matchesBranch && matchesDate;
  });
}, [leaveRequests, leaveSearchText, leaveStatusFilter, selectedBranch, leaveDateRange]);



const filteredPauseRequests = useMemo(() => {
  return pauseRequests.filter(request => {
    const matchesSearch =
      pauseSearchText === '' ||
      request.student_name?.toLowerCase().includes(pauseSearchText.toLowerCase()) ||
      request.student_nickname?.toLowerCase().includes(pauseSearchText.toLowerCase());

    const matchesStatus =
      pauseStatusFilter === 'all' || request.status === pauseStatusFilter;

    const matchesBranch =
      !selectedBranch || request.branch_id === selectedBranch;

    const matchesDate =
      !Array.isArray(pauseDateRange) || pauseDateRange.length === 0 ||
      (dayjs(request.created_at).isSameOrAfter(pauseDateRange[0], 'day') &&
      dayjs(request.created_at).isSameOrBefore(pauseDateRange[1], 'day'));


    return matchesSearch && matchesStatus && matchesBranch && matchesDate;
  });
}, [pauseRequests, pauseSearchText, pauseStatusFilter, selectedBranch, pauseDateRange]);



  const openActionModal = (request, type) => {
    setSelectedRequest(request);
    setRequestType(type);
    setActionModalVisible(true);
    actionForm.setFieldsValue({
      status: request.status,
      admin_notes: request.admin_notes || ''
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'green';
      case 'rejected': return 'red';
      case 'pending': return 'orange';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved': return <CheckCircleOutlined />;
      case 'rejected': return <CloseCircleOutlined />;
      case 'pending': return <HourglassOutlined />;
      default: return <ExclamationCircleOutlined />;
    }
  };

  const getPendingCount = (requests) => {
    return requests.filter(req => req.status === 'pending').length;
  };


    const branchColors = {
  1: "blue",
  2: "green",
  3: "magenta", 
};


  const leaveColumns = [
          {
          title: t("Branch"),
          key: "branch",
          dataIndex: "branch_name",
          width: 120, 
          ellipsis: true, 
          render: (_, record) => {
            const branchId = record.branch_id;
            const color = branchColors[branchId] || "default";
            return record.branch_name ? (
              <Tag color={color}>{record.branch_name}</Tag>
            ) : (
              <Tag>-</Tag>
            );
          },
        },
        {
      title: t('Requested'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      render: (date) => dayjs(date).format('MMM DD, YYYY'),
      sorter: (a, b) => dayjs(a.created_at).unix() - dayjs(b.created_at).unix(),
    },
    {
      title: t('Student'),
      key: 'student',
      width: 200,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.student_name} ({record.student_nickname})</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.student_email}
          </Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.student_phone}
          </Text>
        </Space>
      ),
    },
    {
      title: t('Course / Class'),
      key: 'class',
      width: 180,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Tag color="blue">{record.class_code}</Tag>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.subject_name} ({record.class_id})
          </Text>
        </Space>
      ),
    },
    {
      title: t('Original Schedule'),
      key: 'original',
      width: 150,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text>{dayjs(record.original_date).format('MMM DD, YYYY')}</Text>
          <Text type="secondary">{record.original_time}</Text>
        </Space>
      ),
    },
    {
      title: t('Preferred Schedule'),
      key: 'preferred',
      width: 150,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text>{dayjs(record.preferred_date).format('MMM DD, YYYY')}</Text>
          <Text type="secondary">{record.preferred_time}</Text>
        </Space>
      ),
    },
    {
      title: t('Reason'),
      dataIndex: 'reason',
      key: 'reason',
      width: 200,
      render: (reason) => (
        <Tooltip title={reason}>
          <Text ellipsis>{reason}</Text>
        </Tooltip>
      ),
    },
    {
      title: t('Status'),
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => (
        <Tag color={getStatusColor(status)} icon={getStatusIcon(status)}>
          {status?.toUpperCase()}
        </Tag>
      ),
      filters: [
        { text: 'Pending', value: 'pending' },
        { text: 'Approved', value: 'approved' },
        { text: 'Rejected', value: 'rejected' },
      ],
      onFilter: (value, record) => record.status === value,
    },

    {
      title: t('Approved By'),
      dataIndex: 'approved_by_name',
      key: 'approved_by_name',
      width: 120,
      render: (name) => name || '-',
    },
       {
    title: t('Admin Notes'),
    dataIndex: 'admin_notes',
    key: 'admin_notes',
    render: (notes) => <Text>{notes || ''}</Text>,
    },
    {
      title: t('Actions'),
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => openActionModal(record, 'leave')}
        >
          {t('Review')}
        </Button>
      ),
    }
  ];

  const pauseColumns = [
     {
          title: t("Branch"),
          key: "branch",
          dataIndex: "branch_name",
          width: 120, 
          ellipsis: true, 
          render: (_, record) => {
            const branchId = record.branch_id;
            const color = branchColors[branchId] || "default";
            return record.branch_name ? (
              <Tag color={color}>{record.branch_name}</Tag>
            ) : (
              <Tag>-</Tag>
            );
          },
        },
        {
      title: t('Requested'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      render: (date) => dayjs(date).format('MMM DD, YYYY'),
      sorter: (a, b) => dayjs(a.created_at).unix() - dayjs(b.created_at).unix(),
    },
    {
      title: t('Student'),
      key: 'student',
      width: 200,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.student_name} ({record.student_nickname})</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.student_email}
          </Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.student_phone}
          </Text>
        </Space>
      ),
    },
    {
      title: t('Course'),
      key: 'class',
      width: 180,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Tag color="purple">{record.class_code}</Tag>
        </Space>
      ),
    },
    {
      title: t('Pause Type'),
      dataIndex: 'pause_type',
      key: 'pause_type',
      width: 140,
      render: (type) => (
        <Tag color="orange">{type?.replace('_', ' ').toUpperCase()}</Tag>
      ),
    },
    {
      title: t('Duration'),
      dataIndex: 'planned_duration',
      key: 'planned_duration',
      width: 120,
    },
    {
      title: t('Resume Date'),
      dataIndex: 'resume_date',
      key: 'resume_date',
      width: 120,
      render: (date) => dayjs(date).format('MMM DD, YYYY'),
    },
    {
      title: t('Reason'),
      dataIndex: 'reason',
      key: 'reason',
      width: 200,
      render: (reason) => (
        <Tooltip title={reason}>
          <Text ellipsis>{reason}</Text>
        </Tooltip>
      ),
    },
    {
      title: t('Status'),
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => (
        <Tag color={getStatusColor(status)} icon={getStatusIcon(status)}>
          {status?.toUpperCase()}
        </Tag>
      ),
      filters: [
        { text: 'Pending', value: 'pending' },
        { text: 'Approved', value: 'approved' },
        { text: 'Rejected', value: 'rejected' },
      ],
      onFilter: (value, record) => record.status === value,
    },

    {
      title: t('Approved By'),
      dataIndex: 'approved_by_name',
      key: 'approved_by_name',
      width: 120,
      render: (name) => name || '-',
    },
       {
    title: t('Admin Notes'),
    dataIndex: 'admin_notes',
    key: 'admin_notes',
    render: (notes) => <Text>{notes || ''}</Text>,
    },
    {
      title: t('Actions'),
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => openActionModal(record, 'pause')}
        >
          {t('Review')}
        </Button>
      ),
    }
  ];

  return (
    <div>
<Card> 
    <Title level={3}>
        <PauseCircleOutlined /> Leave & Pause Management
            </Title>
                <Content>
                <Tabs defaultActiveKey="leave">
                <TabPane 
                    tab={
                    <Badge count={getPendingCount(leaveRequests)} offset={[10, 0]}>
                        <span><CalendarOutlined /> {t('Leave Requests')}</span>
                    </Badge>
                    } 
                    key="leave"
                >
               <Card size="small" style={{ marginBottom: 16 }}>
                  <Row
                    gutter={[12, 12]}
                    align="middle"
                    style={{ flexWrap: 'wrap' }}
                  >
                    {userRole === "superadmin" && (
                      <Col xs={24} sm={12} md={4} lg={4}>
                        <Select
                          placeholder={t("Select Branch")}
                          style={{ width: '100%' }}
                          value={selectedBranch}
                          onChange={(value) => setSelectedBranch(value)}
                          allowClear
                          showSearch
                          optionFilterProp="children"
                        >
                          {branches.map((branch) => (
                            <Option key={branch.id} value={branch.id}>
                              {branch.name}
                            </Option>
                          ))}
                        </Select>
                      </Col>
                    )}

                    <Col xs={24} sm={12} md={5} lg={5}>
                      <DatePicker.RangePicker
                        style={{ width: '100%' }}
                        value={leaveDateRange}
                        onChange={(dates) => setLeaveDateRange(dates)}
                        format="DD-MM-YY"
                        allowClear
                      />
                    </Col>

                    <Col xs={24} sm={12} md={6} lg={6}>
                      <Input.Search
                        placeholder={t('Search student name or nickname...')}
                        value={leaveSearchText}
                        onChange={(e) => setLeaveSearchText(e.target.value)}
                        allowClear
                      />
                    </Col>

                    <Col xs={24} sm={12} md={4} lg={4}>
                      <Select
                        placeholder={t('Filter by status')}
                        value={leaveStatusFilter}
                        onChange={setLeaveStatusFilter}
                        style={{ width: '100%' }}
                      >
                        <Option value="all">{t('All Status')}</Option>
                        <Option value="pending">{t('Pending')}</Option>
                        <Option value="approved">{t('Approved')}</Option>
                        <Option value="rejected">{t('Rejected')}</Option>
                      </Select>
                    </Col>

                    <Col xs={24} sm={12} md={5} lg={5} style={{ textAlign: 'right' }}>
                      <Space>
                        <Text type="secondary">{t('Total')}: {filteredLeaveRequests.length}</Text>
                        <Text type="secondary">{t('Pending')}: {getPendingCount(filteredLeaveRequests)}</Text>
                      </Space>
                    </Col>
                  </Row>
                </Card>

                    <Table
                        columns={leaveColumns}
                        dataSource={filteredLeaveRequests}
                        rowKey="id"
                        loading={loading}
                        pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) =>
                            `${range[0]}-${range[1]} of ${total} requests`,
                        }}
                        scroll={{ x: 'max-content' }}
                    />
                    
                </TabPane>

                <TabPane 
                    tab={
                    <Badge count={getPendingCount(pauseRequests)} offset={[10, 0]}>
                        <span><PauseOutlined /> {t('Pause Requests')}</span>
                    </Badge>
                    } 
                    key="pause"
                >
                     <Card size="small" style={{ marginBottom: 16 }}>
                    <Row
                      gutter={[12, 12]}
                      align="middle"
                      style={{ flexWrap: 'wrap' }}
                    >
                      {userRole === "superadmin" && (
                        <Col xs={24} sm={12} md={4} lg={4}>
                          <Select
                            placeholder={t("Select Branch")}
                            style={{ width: '100%' }}
                            value={selectedBranch}
                            onChange={(value) => setSelectedBranch(value)}
                            allowClear
                            showSearch
                            optionFilterProp="children"
                          >
                            {branches.map((branch) => (
                              <Option key={branch.id} value={branch.id}>
                                {branch.name}
                              </Option>
                            ))}
                          </Select>
                        </Col>
                      )}

                      <Col xs={24} sm={12} md={5} lg={5}>
                        <DatePicker.RangePicker
                          style={{ width: '100%' }}
                          value={pauseDateRange}
                          onChange={(dates) => setPauseDateRange(dates)}
                          format="DD-MM-YY"
                          allowClear
                        />
                      </Col>

                      <Col xs={24} sm={12} md={6} lg={6}>
                        <Input.Search
                          placeholder={t('Search student name or nickname...')}
                          value={pauseSearchText}
                          onChange={(e) => setPauseSearchText(e.target.value)}
                          allowClear
                        />
                      </Col>

                      <Col xs={24} sm={12} md={4} lg={4}>
                        <Select
                          placeholder={t('Filter by status')}
                          value={pauseStatusFilter}
                          onChange={setPauseStatusFilter}
                          style={{ width: '100%' }}
                        >
                          <Option value="all">{t('All Status')}</Option>
                          <Option value="pending">{t('Pending')}</Option>
                          <Option value="approved">{t('Approved')}</Option>
                          <Option value="rejected">{t('Rejected')}</Option>
                        </Select>
                      </Col>

                      <Col xs={24} sm={12} md={5} lg={5} style={{ textAlign: 'right' }}>
                        <Space>
                          <Text type="secondary">{t('Total')}: {filteredPauseRequests.length}</Text>
                          <Text type="secondary">{t('Pending')}: {getPendingCount(filteredPauseRequests)}</Text>
                        </Space>
                      </Col>
                    </Row>
                  </Card>

                    <Table
                        columns={pauseColumns}
                        dataSource={filteredPauseRequests}
                        rowKey="id"
                        loading={loading}
                        pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) =>
                            `${range[0]}-${range[1]} of ${total} requests`,
                        }}
                        scroll={{ x: 'max-content' }}
                    />
                   
                </TabPane>
                </Tabs>
                <Modal
                    title={
                        <Space>
                        {requestType === 'leave' ? <CalendarOutlined /> : <PauseOutlined />}
                        {t('Review')} {requestType === 'leave' ? t('Leave') : t('Pause')} {t('Request')}
                        </Space>
                    }
                    visible={actionModalVisible}
                    onCancel={() => setActionModalVisible(false)}
                    footer={null}
                    width={700}
                    >
                    {selectedRequest && (
                        <>
                        {/* Student Info Card */}
                        <Card size="small" style={{ marginBottom: 16, backgroundColor: '#fafafa' }}>
                            <Row gutter={16}>
                            <Col span={16}>
                                <Space direction="vertical" size={2}>
                                <Text strong style={{ fontSize: '16px' }}>{selectedRequest.student_name}</Text>
                                <Text type="secondary">{selectedRequest.student_email}</Text>
                                </Space>
                            </Col>
                            <Col span={8} style={{ textAlign: 'right' }}>
                                <Space direction="vertical" size={2} style={{ alignItems: 'flex-end' }}>
                                <Tag color={getStatusColor(selectedRequest.status)} icon={getStatusIcon(selectedRequest.status)}>
                                    {selectedRequest.status?.toUpperCase()}
                                </Tag>
                                {selectedRequest.approved_by_name && (
                                    <Text type="secondary" style={{ fontSize: '12px' }}>
                                    By: {selectedRequest.approved_by_name}
                                    </Text>
                                )}
                                </Space>
                            </Col>
                            </Row>
                        </Card>

                        {/* Request Details */}
                        <div style={{ marginBottom: 20 }}>
                            {requestType === 'leave' ? (
                            <>
                                {/* Class Info */}
                                <div style={{ marginBottom: 16 }}>
                                <Text type="secondary" style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    CLASS INFORMATION
                                </Text>
                                <div style={{ marginTop: 4 }}>
                                    <Text strong style={{ fontSize: '15px' }}>
                                    {selectedRequest.class_code} - {selectedRequest.subject_name} ({selectedRequest.class_id})
                                    </Text>
                                </div>
                                </div>

                                {/* Schedule Info */}
                                <Row gutter={24} style={{ marginBottom: 16 }}>
                                <Col span={12}>
                                    <div>
                                    <Text type="secondary" style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        ORIGINAL SCHEDULE
                                    </Text>
                                    <div style={{ marginTop: 4 }}>
                                        <div><Text strong>{dayjs(selectedRequest.original_date).format('MMM DD, YYYY')}</Text></div>
                                        <div><Text>{selectedRequest.original_time}</Text></div>
                                    </div>
                                    </div>
                                </Col>
                                <Col span={12}>
                                    <div>
                                    <Text type="secondary" style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        PREFERRED SCHEDULE
                                    </Text>
                                    <div style={{ marginTop: 4 }}>
                                        <div><Text strong>{dayjs(selectedRequest.preferred_date).format('MMM DD, YYYY')}</Text></div>
                                        <div><Text>{selectedRequest.preferred_time}</Text></div>
                                    </div>
                                    </div>
                                </Col>
                                </Row>
                            </>
                            ) : (
                            <>
                                {/* Course Info */}
                                <div style={{ marginBottom: 16 }}>
                                <Text type="secondary" style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    COURSE INFORMATION
                                </Text>
                                <div style={{ marginTop: 4 }}>
                                    <Text strong style={{ fontSize: '15px' }}>
                                    {selectedRequest.class_code} - {selectedRequest.subject_name} ({selectedRequest.class_id})
                                    </Text>
                                </div>
                                </div>

                                {/* Pause Details */}
                                <Row gutter={24} style={{ marginBottom: 16 }}>
                                <Col span={8}>
                                    <div>
                                    <Text type="secondary" style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        PAUSE TYPE
                                    </Text>
                                    <div style={{ marginTop: 4 }}>
                                        <Tag color="orange" style={{ margin: 0 }}>
                                        {selectedRequest.pause_type?.replace('_', ' ').toUpperCase()}
                                        </Tag>
                                    </div>
                                    </div>
                                </Col>
                                <Col span={8}>
                                    <div>
                                    <Text type="secondary" style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        DURATION
                                    </Text>
                                    <div style={{ marginTop: 4 }}>
                                        <Text strong>{selectedRequest.planned_duration}</Text>
                                    </div>
                                    </div>
                                </Col>
                                <Col span={8}>
                                    <div>
                                    <Text type="secondary" style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        RESUME DATE
                                    </Text>
                                    <div style={{ marginTop: 4 }}>
                                        <Text strong>{dayjs(selectedRequest.resume_date).format('MMM DD, YYYY')}</Text>
                                    </div>
                                    </div>
                                </Col>
                                </Row>
                            </>
                            )}

                            {/* Reason */}
                            <div style={{ marginBottom: 16 }}>
                            <Text type="secondary" style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                REASON
                            </Text>
                            <div style={{ 
                                marginTop: 4, 
                                padding: '12px', 
                                backgroundColor: '#f5f5f5', 
                                borderRadius: '6px',
                                border: '1px solid #e8e8e8'
                            }}>
                                <Text>{selectedRequest.reason}</Text>
                            </div>
                            </div>

                            {/* Request Date */}
                            <div style={{ textAlign: 'right' }}>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                Requested on {dayjs(selectedRequest.created_at).format('MMM DD, YYYY HH:mm')}
                            </Text>
                            </div>
                        </div>

                        <Divider />

                        <Form
                            form={actionForm}
                            layout="vertical"
                            onFinish={handleStatusUpdate}
                        >
                            <Form.Item
                            name="status"
                            label={t('Status')}
                            rules={[{ required: true, message: t('Please select status') }]}
                            >
                            <Select>
                                <Option value="pending">
                                <Tag color="orange" icon={<HourglassOutlined />}>PENDING</Tag>
                                </Option>
                                <Option value="approved">
                                <Tag color="green" icon={<CheckCircleOutlined />}>APPROVED</Tag>
                                </Option>
                                <Option value="rejected">
                                <Tag color="red" icon={<CloseCircleOutlined />}>REJECTED</Tag>
                                </Option>
                            </Select>
                            </Form.Item>

                            <Form.Item
                            name="admin_notes"
                            label={t('Admin Notes')}
                            >
                            <Input.TextArea 
                                rows={4} 
                                placeholder={t('Add notes about this decision...')}
                            />
                            </Form.Item>

                            <Form.Item>
                            <Space>
                                <Button type="primary" htmlType="submit">
                                {t('Update Request')}
                                </Button>
                                <Button onClick={() => setActionModalVisible(false)}>
                                {t('Cancel')}
                                </Button>
                            </Space>
                            </Form.Item>
                        </Form>
                        </>
                    )}
                    </Modal>
                </Content>
            </Card>
        </div>
        );
     }

export default withRoleAccess(AdminLeaveManagement, ['admin', 'superadmin']);